import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Link from "@mui/joy/Link";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import { BlockError, BlockLoading, ScrollTable } from "../../../bids/analytics/components/AnalyticsShared";
import { AsyncBlockState } from "../../../bids/analytics/hooks";
import { formatInt, formatMoneyKopecks, toNum } from "../../../bids/analytics/format";
import { OrderFailReason } from "../../../../models/order";
import { FailDetailGroup, FailDetailOther, OrderFailDetailsReport } from "../../../../models/orderAnalytics";
import { ordersFailDetailsApi } from "../ordersAnalyticsApi";
import { groupsForReason, orderUrl, otherGroupsLabel, signatureListUrl, steamCodeLabel } from "../failDetails";
import { culpritLabel, failReasonLabel } from "../failReasons";
import { formatPercent, ListContext } from "./FailReasonsCharts";

/** Две строки текста, длинные слова переносятся — чтобы комментарий не раздвигал таблицу */
const CLAMP_SX = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
} as const;

/** Кеш текстов по причине; ключ включает фильтр, поэтому смена периода или игры ведёт к новой загрузке */
export type DetailsCache = Map<string, OrderFailDetailsReport>;

type DetailsState = { key: string | null; error: string | null; data: OrderFailDetailsReport | null };

function cacheKey(reason: OrderFailReason, ctx: ListContext): string {
    return `${reason}|${ctx.gameId ?? ""}|${ctx.from.getTime()}|${ctx.to.getTime()}`;
}

/**
 * Тексты ошибок одной причины под её строкой. Причину загружает отдельным запросом (у каждой свой топ-200),
 * «Без причины» берёт из общего отчёта — фильтром её не запросить.
 * refreshToken растёт при «Обновить»: раскрытые причины перезагружаются, ответы старых запросов отбрасываются.
 */
export function ReasonDetails({ reason, ctx, cache, full, onFullRetry, refreshToken }: {
    reason: OrderFailReason | null;
    ctx: ListContext;
    cache: DetailsCache;
    full: AsyncBlockState<OrderFailDetailsReport>;
    onFullRetry: () => void;
    refreshToken: number;
}) {
    const [state, setState] = React.useState<DetailsState>({ key: null, error: null, data: null });
    const [retry, setRetry] = React.useState(0);
    const key = reason === null ? null : cacheKey(reason, ctx);

    React.useEffect(() => {
        if (reason === null || key === null) {
            return;
        }
        const cached = cache.get(key);
        if (cached) {
            setState({ key, error: null, data: cached });
            return;
        }
        let cancelled = false;
        ordersFailDetailsApi({ from: ctx.from, to: ctx.to, gameId: ctx.gameId, failReason: reason })
            .then((result) => {
                if (cancelled) {
                    return;
                }
                if (result.result && result.value) {
                    cache.set(key, result.value);
                    setState({ key, error: null, data: result.value });
                } else {
                    setState({ key, error: "Не удалось загрузить тексты ошибок", data: null });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setState({ key, error: "Ошибка запроса", data: null });
                }
            });
        return () => {
            cancelled = true;
        };
        // ctx целиком закодирован в key
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, reason, cache, refreshToken, retry]);

    if (reason === null) {
        return <NoReasonDetails full={full} ctx={ctx} onRetry={onFullRetry} />;
    }
    // Данные другого ключа (смена фильтра) не показываем — ждём свои
    if (state.key !== key || (!state.data && !state.error)) {
        return <BlockLoading />;
    }
    if (state.error) {
        return <BlockError message={state.error} onRetry={() => setRetry((r) => r + 1)} />;
    }
    return state.data ? <SignaturesTable groups={state.data.groups} other={state.data.other} ctx={ctx} /> : null;
}

/** «Без причины» — из общего топа-200 по всем причинам; если часть ушла в хвост, об этом подписано */
function NoReasonDetails({ full, ctx, onRetry }: {
    full: AsyncBlockState<OrderFailDetailsReport>;
    ctx: ListContext;
    onRetry: () => void;
}) {
    if (full.error) {
        return <BlockError message={full.error} onRetry={onRetry} />;
    }
    if (full.loading || !full.data) {
        return <BlockLoading />;
    }
    const groups = groupsForReason(full.data, null);
    const isTruncated = toNum(full.data.other.groupsCount) > 0;
    return (
        <>
            {groups.length > 0
                ? <SignaturesTable groups={groups} other={null} ctx={ctx} />
                : !isTruncated && <Typography level="body-sm" color="neutral">Текстов ошибок нет.</Typography>}
            {isTruncated && (
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                    Тексты без причины берутся из общего топа (200 текстов по всем причинам) — часть из них может
                    быть не показана.
                </Typography>
            )}
        </>
    );
}

function SignatureText({ group, ctx }: { group: FailDetailGroup; ctx: ListContext }) {
    const url = signatureListUrl(group, ctx);
    return (
        <Tooltip
            variant="outlined"
            placement="top-start"
            title={
                <Box sx={{ maxWidth: 520, maxHeight: "60vh", overflow: "auto", whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere", wordBreak: "break-word" }}>
                    {group.exampleComment || group.signature}
                </Box>
            }
        >
            {/* Без ссылки текст получает фокус, чтобы пример комментария был доступен с клавиатуры */}
            <Typography level="body-sm" sx={CLAMP_SX} tabIndex={url ? undefined : 0}>
                {url
                    ? <Link component={RouterLink} target="_blank" rel="noopener noreferrer" to={url} sx={{ display: "inline" }}>{group.signature}</Link>
                    : group.signature}
            </Typography>
        </Tooltip>
    );
}

function SignaturesTable({ groups, other, ctx }: {
    groups: FailDetailGroup[];
    other: FailDetailOther | null;
    ctx: ListContext;
}) {
    if (groups.length === 0) {
        return <Typography level="body-sm" color="neutral">Текстов ошибок нет.</Typography>;
    }
    const otherLabel = other ? otherGroupsLabel(other) : null;

    return (
        <ScrollTable>
            <Table size="sm" variant="soft" sx={{ tableLayout: "fixed", minWidth: 1280 }}>
                <thead>
                    <tr>
                        <th scope="col" style={{ width: "38%" }}>Текст ошибки</th>
                        <th scope="col" style={{ width: 130 }}>Код Steam</th>
                        <th scope="col" style={{ width: 130 }}>Виновник</th>
                        <th scope="col" style={{ width: 80 }}>Позиций</th>
                        <th scope="col" style={{ width: 80 }}>Ордеров</th>
                        <th scope="col" style={{ width: 100 }}>В причине</th>
                        <th scope="col" style={{ width: 130 }}>От неуспешных</th>
                        <th scope="col" style={{ width: 120 }}>Сумма</th>
                        <th scope="col" style={{ width: 120 }}>Последний</th>
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g) => (
                        <tr key={`${g.culprit}|${g.steamCode ?? ""}|${g.signature}`}>
                            <td><SignatureText group={g} ctx={ctx} /></td>
                            <td>{steamCodeLabel(g.steamCode, g.steamCodeName)}</td>
                            <td>{culpritLabel(g.culprit)}</td>
                            <td>{formatInt(g.count)}</td>
                            <td>{formatInt(g.ordersCount)}</td>
                            <td>{formatPercent(g.percentOfReason)}</td>
                            <td>{formatPercent(g.percentOfFailed)}</td>
                            <td>{formatMoneyKopecks(g.sum)}</td>
                            <td>
                                <Link component={RouterLink} target="_blank" rel="noopener noreferrer" to={orderUrl(g.lastOrderId)}>#{g.lastOrderId}</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {otherLabel && <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>{otherLabel}</Typography>}
        </ScrollTable>
    );
}

/** Коды ошибок Steam из комментариев за период */
export function SteamCodesTable({ report }: { report: OrderFailDetailsReport }) {
    if (report.steamCodes.length === 0) {
        return <Typography level="body-sm" color="neutral">В комментариях нет кодов ошибок Steam.</Typography>;
    }
    return (
        <ScrollTable>
            <Table size="sm" sx={{ minWidth: 820 }}>
                <thead>
                    <tr>
                        <th scope="col" style={{ width: 200 }}>Код</th>
                        <th scope="col" style={{ width: 110 }}>Количество</th>
                        <th scope="col" style={{ width: 130 }}>От неуспешных</th>
                        <th scope="col" style={{ width: 130 }}>Сумма</th>
                        <th scope="col">Чаще всего в причинах</th>
                    </tr>
                </thead>
                <tbody>
                    {report.steamCodes.map((c) => (
                        <tr key={c.code}>
                            <td>{steamCodeLabel(c.code, c.name)}</td>
                            <td>{formatInt(c.count)}</td>
                            <td>{formatPercent(c.percentOfFailed)}</td>
                            <td>{formatMoneyKopecks(c.sum)}</td>
                            <td>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {c.reasons.map((r) => (
                                        <Chip key={String(r.failReason ?? "none")} size="sm" variant="soft">
                                            {failReasonLabel(r.failReason)} {formatInt(r.count)}
                                        </Chip>
                                    ))}
                                </Box>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ScrollTable>
    );
}
